<template>
    <div class="wa-channel-switcher">
        <div
            class="wa-channel-switcher__item"
            :class="{ 'wa-channel-switcher__item--active': selectedChannelId === null }"
            @click="$emit('change', null)"
            v-tippy="{ placement: 'right', content: 'Tüm Kanallar' }"
        >
            <div class="wa-channel-switcher__avatar wa-channel-switcher__avatar--all">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 8L4 8l16-2-8 6zm8 6H4V9.62l8 4.97 8-4.97V18z" fill="currentColor"/>
                </svg>
            </div>
            <div class="wa-channel-switcher__badge" v-if="totalUnread > 0">
                {{ totalUnread > 99 ? "99+" : totalUnread }}
            </div>
        </div>

        <div class="wa-channel-switcher__divider" />

        <div
            v-for="channel in channels"
            :key="channel._id"
            class="wa-channel-switcher__item"
            :class="{ 'wa-channel-switcher__item--active': selectedChannelId === channel._id }"
            @click="$emit('change', channel._id)"
            v-tippy="{ placement: 'right', content: getChannelTooltip(channel) }"
        >
            <div
                class="wa-channel-switcher__avatar"
                :class="getChannelAvatarClass(channel)"
            >
                <span v-if="channel.name">{{ getChannelInitials(channel) }}</span>
                <svg v-else viewBox="0 0 24 24" width="20" height="20" fill="none">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="currentColor"/>
                    <path d="M12.004 2.003A9.988 9.988 0 002.017 12c0 1.762.46 3.418 1.265 4.856L2 22l5.293-1.249A9.953 9.953 0 0012.004 22c5.514 0 9.996-4.478 9.996-9.997C22 6.482 17.517 2.003 12.004 2.003z" stroke="currentColor" stroke-width="1.5" fill="none"/>
                </svg>
            </div>
            <div class="wa-channel-switcher__status-dot" :class="getStatusDotClass(channel)" />
        </div>

        <div class="wa-channel-switcher__spacer" />

        <div
            class="wa-channel-switcher__item wa-channel-switcher__item--settings"
            @click="$emit('open-settings')"
            v-tippy="{ placement: 'right', content: 'Kanal Ayarları' }"
        >
            <div class="wa-channel-switcher__avatar wa-channel-switcher__avatar--action">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" fill="currentColor"/>
                </svg>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "WAChannelSwitcher",
    props: {
        channels: {
            type: Array,
            default: () => [],
        },
        selectedChannelId: {
            type: String,
            default: null,
        },
        totalUnread: {
            type: Number,
            default: 0,
        },
    },
    methods: {
        getChannelInitials(channel) {
            const name = channel.name || "";
            return name
                .split(" ")
                .slice(0, 2)
                .map((w) => w[0])
                .join("")
                .toUpperCase();
        },
        getChannelTooltip(channel) {
            const typeLabel = this.getChannelTypeLabel(channel.type);
            const name = channel.name || channel.payload && channel.payload.phoneNumber || typeLabel;
            return name + " · " + typeLabel;
        },
        getChannelTypeLabel(type) {
            const labels = {
                "whatsapp-business-api": "WABA",
                "whatsapp-business-api-supsis": "WABA Supsis",
                "whatsapp-unipile": "Unipile",
                "whatsapp-v2": "WhatsApp QR",
                "whatsapp-qr": "WhatsApp QR",
            };
            return labels[type] || "WhatsApp";
        },
        getChannelAvatarClass(channel) {
            const classMap = {
                "whatsapp-business-api": "wa-channel-switcher__avatar--waba",
                "whatsapp-business-api-supsis": "wa-channel-switcher__avatar--waba",
                "whatsapp-unipile": "wa-channel-switcher__avatar--unipile",
                "whatsapp-v2": "wa-channel-switcher__avatar--qr",
                "whatsapp-qr": "wa-channel-switcher__avatar--qr",
            };
            return classMap[channel.type] || "";
        },
        getStatusDotClass(channel) {
            if (!channel.enabled) return "wa-channel-switcher__status-dot--disabled";
            return "wa-channel-switcher__status-dot--active";
        },
    },
};
</script>

<style lang="scss" scoped>
.wa-channel-switcher {
    width: 60px;
    background-color: #202c33;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 0;
    gap: 4px;
    overflow: hidden;
    flex-shrink: 0;

    &__divider {
        width: 36px;
        height: 1px;
        background: rgba(255, 255, 255, 0.1);
        margin: 4px 0;
        flex-shrink: 0;
    }

    &__spacer {
        flex: 1;
    }

    &__item {
        position: relative;
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background-color 0.15s ease;
        flex-shrink: 0;

        &:hover {
            background-color: rgba(255, 255, 255, 0.08);
        }

        &--active {
            background-color: rgba(37, 211, 102, 0.15);

            .wa-channel-switcher__avatar {
                border-color: #25D366;
                color: #25D366;
            }
        }

        &--settings {
            .wa-channel-switcher__avatar {
                color: #667781;
            }

            &:hover .wa-channel-switcher__avatar {
                color: #aebac1;
            }
        }
    }

    &__avatar {
        width: 38px;
        height: 38px;
        border-radius: 50%;
        background: #2a3942;
        border: 1.5px solid #3b4a54;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #aebac1;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: -0.5px;
        overflow: hidden;
        transition: border-color 0.15s;

        &--all {
            background: #1f2c33;
            color: #25D366;
            border-color: #2a3942;
        }

        &--waba {
            background: #0b5b4e;
            color: #25D366;
            border-color: #128C7E;
        }

        &--unipile {
            background: #1a3a4a;
            color: #53bdeb;
            border-color: #29596b;
        }

        &--qr {
            background: #1e2f38;
            color: #aebac1;
        }

        &--action {
            background: transparent;
            border-color: transparent;
            width: 36px;
            height: 36px;
        }
    }

    &__badge {
        position: absolute;
        top: 0;
        right: 0;
        background: #25D366;
        color: #fff;
        border-radius: 10px;
        font-size: 10px;
        font-weight: 700;
        min-width: 18px;
        height: 18px;
        line-height: 18px;
        text-align: center;
        padding: 0 4px;
        border: 2px solid #202c33;
    }

    &__status-dot {
        position: absolute;
        bottom: 2px;
        right: 2px;
        width: 9px;
        height: 9px;
        border-radius: 50%;
        border: 2px solid #202c33;

        &--active {
            background: #25D366;
        }

        &--disabled {
            background: #667781;
        }
    }
}
</style>
